import React, { FC, useState, useEffect } from 'react';
import { Box, Popover, styled, TextField } from '@mui/material';
import {
  PickersDay,
  PickersDayProps,
  StaticDatePicker,
} from '@mui/x-date-pickers';
import clsx from 'clsx';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import Button from '@/components/ui/Button';

interface IProps {
  dateRange: [Date | null, Date | null];
  onApply: (dateRange: [Date | null, Date | null]) => void;
}

interface CustomPickerDayProps extends PickersDayProps<Moment> {
  dayIsBetween: boolean;
  isFirstDay: boolean;
  isLastDay: boolean;
}

const CustomPickersDay = styled(PickersDay<Moment>, {
  shouldForwardProp: (prop) =>
    prop !== 'dayIsBetween' && prop !== 'isFirstDay' && prop !== 'isLastDay',
})<CustomPickerDayProps>(({ dayIsBetween, isFirstDay, isLastDay }) => ({
  ...(dayIsBetween && {
    borderRadius: 0,
    backgroundColor: '#EBEFF2',
    color: '#43505D',
  }),
  ...(isFirstDay && {
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
    backgroundColor: '#0F253B',
    color: 'white',
  }),
  ...(isLastDay && {
    borderTopRightRadius: '50%',
    borderBottomRightRadius: '50%',
    backgroundColor: '#0F253B',
    color: 'white',
  }),
}));

const DateFilter: FC<IProps> = ({ dateRange, onApply }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [tempDateRange, setTempDateRange] = useState<
    [Moment | null, Moment | null]
  >([null, null]);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!open) {
      setTempDateRange([
        dateRange[0] ? moment(dateRange[0]) : null,
        dateRange[1] ? moment(dateRange[1]) : null,
      ]);
    }
  }, [dateRange, open]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleApply = () => {
    onApply([
      tempDateRange[0] ? tempDateRange[0].toDate() : null,
      tempDateRange[1] ? tempDateRange[1].toDate() : null,
    ]);
    handleClose();
  };

  const handleDateChange = (date: Moment | null) => {
    const [start, end] = tempDateRange;

    if (!start || (start && end)) {
      setTempDateRange([date, null]);
    } else if (date && date.isAfter(start)) {
      setTempDateRange([start, date]);
    } else {
      setTempDateRange([date, null]);
    }
  };

  const renderDay = (
    date: Moment,
    selectedDates: Array<Moment | null>,
    pickersDayProps: PickersDayProps<Moment>
  ) => {
    const [start, end] = tempDateRange;

    if (!start) {
      return <PickersDay {...pickersDayProps} />;
    }

    const dayIsBetween = end ? date.isBetween(start, end, 'day') : false;
    const isFirstDay = date.isSame(start, 'day');
    const isLastDay = date.isSame(end, 'day');

    return (
      <CustomPickersDay
        {...pickersDayProps}
        disableMargin
        dayIsBetween={dayIsBetween}
        isFirstDay={isFirstDay}
        isLastDay={isLastDay}
      />
    );
  };

  return (
    <div>
      <div
        className="relative h-10 w-full"
        role="presentation"
        onClick={handleClick}
      >
        <input
          readOnly
          className={clsx(
            'w-full h-full cursor-pointer rounded border bg-white px-3 py-2 text-body2 placeholder:text-primary placeholder:font-bold',
            'border-grey-300 outline-primary',
            'focus:outline-1'
          )}
          placeholder="Filter by date / range"
        />
        <i
          className={clsx(
            'pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[20px] text-primary',
            open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
          )}
        />
      </div>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={open}
        PaperProps={{
          className:
            'shadow-[0px_4px_12px_-4px_rgba(64,64,64,0.20)] rounded-lg',
        }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={handleClose}
      >
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Box className="p-4 flex flex-col gap-3">
            <StaticDatePicker
              disableFuture
              displayStaticWrapperAs="desktop"
              renderDay={renderDay}
              renderInput={(params) => <TextField {...params} />}
              value={tempDateRange[0] || tempDateRange[1]}
              onChange={handleDateChange}
            />
            <Button
              className="w-full"
              label="Apply filter"
              size="small"
              variant="success"
              onClick={handleApply}
            />
          </Box>
        </LocalizationProvider>
      </Popover>
    </div>
  );
};

export default DateFilter;
